/**
 * The header bell (docs/NOTIFICATIONS.md "NotificationBell").
 *
 * A Popover over the selected org's notifications with a "Mark all read"
 * header action — a menu surface, not a page list (ruling in
 * NOTIFICATIONS.md; a future /notifications page would be an index grid).
 * Two views: `unread` (the default — the queue; a row leaves it the moment
 * it is read) and `all` (the newest 30, read or not — the safety net for a
 * mis-click), switched by the footer link and reset to `unread` on close.
 * It mounts `useNotifications`, which owns the single always-on Realtime
 * subscription, so this component must stay mounted on every AppLayout page —
 * including the org-less ones, where it renders disabled.
 */
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Bell, BellDot, CheckCheck, History, Loader2 } from "lucide-react";
import { formatInstantInZone } from "@releve/shared/format/date";
import type { NotificationListStatus, NotificationWire } from "@releve/shared";
import { Button } from "@/components/ui/button";
import { CountBadge } from "@/components/ui/count-badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useMarkNotificationsRead, useNotifications } from "@/hooks/useNotifications";
import { useMemberDirectory } from "@/hooks/useMemberDirectory";
import { useOrgNavigate } from "@/hooks/useOrgNavigate";
import { useTranslation } from "@/hooks/useTranslation";
import { getDateFnsLocale } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";
import { renderNotification } from "./renderers";

export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const orgTimeZone = selectedOrganization?.timezone ?? null;
  const orgNavigate = useOrgNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<NotificationListStatus>("unread");

  const { data, isLoading } = useNotifications(orgId, view);
  const markRead = useMarkNotificationsRead(orgId);
  const { nameOf } = useMemberDirectory(orgId);

  const items = data?.items ?? [];
  const unread = data?.unread_count ?? 0;
  const dateFnsLocale = getDateFnsLocale(i18n.language);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Every open starts on the queue; "all" is a detour, not a preference.
    if (!next) setView("unread");
  };

  const handleRowClick = (n: NotificationWire) => {
    if (!n.read_at) markRead.mutate({ ids: [n.id] });
    if (n.link) orgNavigate(n.link);
    handleOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 relative"
          aria-label={t("notifications.title")}
          disabled={!orgId}
        >
          <Bell className="h-4 w-4" />
          <CountBadge
            count={unread}
            className="absolute -top-1 -right-1 bg-header-badge text-header-badge-foreground"
          />
          {unread > 0 && (
            <span className="sr-only">{t("notifications.ariaUnread", { count: unread })}</span>
          )}
        </Button>
      </PopoverTrigger>
      {/* Same frame as the org switcher's popover: p-0, the success accent
          border, an h-11 px-3 top row, p-1 rounded rows, a p-2 footer with a
          full-width ghost button. */}
      <PopoverContent className="w-[calc(100vw-2rem)] sm:w-96 p-0 border-success" align="end">
        <div className="flex h-11 items-center justify-between border-b px-3">
          <span className="text-sm font-medium">{t("notifications.title")}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            disabled={unread === 0 || markRead.isPending}
            onClick={() => markRead.mutate({ all: true })}
          >
            <CheckCheck className="h-4 w-4" aria-hidden="true" />
            {t("notifications.markAllRead")}
          </Button>
        </div>

        <ScrollArea className="max-h-[70vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            </div>
          ) : items.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm">
              <h3 className="font-medium">
                {t(view === "unread" ? "notifications.empty.title" : "notifications.empty.allTitle")}
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">{t("notifications.empty.body")}</p>
            </div>
          ) : (
            <ul className="flex flex-col p-1">
              {items.map((n) => {
                const { icon: Icon, title, message } = renderNotification(n, t, nameOf);
                const isUnread = n.read_at === null;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      className={cn(
                        "flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground",
                        isUnread && "bg-primary/5",
                      )}
                      onClick={() => handleRowClick(n)}
                    >
                      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
                      <span className="min-w-0 flex-1 flex flex-col gap-0.5">
                        <span className="font-medium">{title}</span>
                        {message && (
                          <span className="font-light text-muted-foreground line-clamp-2">{message}</span>
                        )}
                        <span
                          className="text-xs text-muted-foreground"
                          title={formatInstantInZone(n.created_at, orgTimeZone, i18n.language, "dateTime")}
                        >
                          {formatDistanceToNow(new Date(n.created_at), {
                            addSuffix: true,
                            locale: dateFnsLocale,
                          })}
                        </span>
                      </span>
                      {isUnread && (
                        <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>

        <div className="border-t p-2">
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-sm"
            onClick={() => setView(view === "unread" ? "all" : "unread")}
          >
            {view === "unread" ? (
              <History className="h-4 w-4" aria-hidden="true" />
            ) : (
              <BellDot className="h-4 w-4" aria-hidden="true" />
            )}
            {t(view === "unread" ? "notifications.showAll" : "notifications.showUnread")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
